import './applianceDetails.scss';

import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import ApplianceOptions from './ApplianceOptions';
import ApplianceSpecs from './ApplianceSpecs';
import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { GetDocDisplayName } from '@progymedia/sbi-web-occ/src/utils';
import { Icon } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { Remarkable } from 'remarkable';
import arrowIcon from '../../images/icons/Icon-Fleche.svg';
import iconMoins from '../../images/icons/Icon-Moins.svg';
import iconPlus from '../../images/icons/Icon-Plus.svg';
import manualIcon from '@progymedia/sbi-web-occ/src/images/icons/icon-Manuels-Page-Icon-2.svg';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

function ApplianceDetailsSection(props: {
    title: string;
    setActiveSection?: any;
    activeSection?: any;
    toggle?: boolean;
    link?: string;
    icon?: any;
    setPage?: any;
    renderCurrentSection?: any;
}) {
    const { navigateTranslated } = usePageLocalization();
    const currentDevice = useMobileDetect();
    const {
        title,
        setActiveSection,
        activeSection,
        toggle,
        link,
        icon,
        setPage,
        renderCurrentSection,
    } = props;

    return (
        <div className='appliance-details-section'>
            <div
                className='section-title'
                onClick={() => {
                    toggle
                        ? title === activeSection
                            ? setActiveSection('unselected')
                            : setActiveSection(title)
                        : link && navigateTranslated(link);

                    setPage && setPage(1);
                }}
            >
                <span
                    className={
                        title === activeSection ? 'title selected' : 'title'
                    }
                >
                    {icon && <span className='icon'>{icon}</span>}

                    {title}
                </span>

                {toggle && (
                    <img
                        className='toggle-section'
                        src={title === activeSection ? iconMoins : iconPlus}
                        alt=''
                    />
                )}
            </div>

            {currentDevice.isMobile() &&
                title === activeSection &&
                renderCurrentSection &&
                renderCurrentSection()}
        </div>
    );
}

function ApplianceManuals(props: { documents: any; applianceCode: string }) {
    const { currentLocale } = usePageLocalization();
    const { documents, applianceCode } = props;
    const { t } = useTranslation('layout');

    const sortedDocuments: any[] = [];
    documents?.map((document: any) => {
        const displayName = GetDocDisplayName(document, applianceCode);
        let priority = Number.MAX_VALUE;

        if (
            displayName?.toLowerCase()?.indexOf('manual') >= 0 ||
            displayName?.toLowerCase()?.indexOf('manuel') >= 0
        ) {
            priority = 3;
        }
        switch (displayName) {
            case t('document.bilingual-user'):
            case t('document.install-user'):
            case t('document.user-manual'):
                priority = 1;
                break;
            case t('document.bilingual-install'):
            case t('document.manual'):
                priority = 2;
                break;
            case t('document.dimensions'):
                priority = 4;
                break;
            default:
                break;
        }
        sortedDocuments.push({
            displayName: displayName,
            document: document,
            priority: priority,
        });
    });

    sortedDocuments.sort((a: any, b: any) => {
        if (a.priority === b.priority && a.displayName < b.displayName) {
            return -1;
        } else if (a.priority === b.priority && a.displayName > b.displayName) {
            return 1;
        }
        return a.priority - b.priority;
    });

    return (
        <div className='documents'>
            {sortedDocuments?.map((sortedDocument: any, index: number) => {
                return (
                    <a
                        key={index}
                        rel='noreferrer'
                        target='_blank'
                        href={
                            '/file/' + sortedDocument?.document?.[currentLocale]
                        }
                        className='document'
                    >
                        <img width={30} height={57} src={manualIcon} alt='--' />

                        <span>{sortedDocument?.displayName} (PDF)</span>
                    </a>
                );
            })}
        </div>
    );
}

function ApplianceParts(props: { partsMessage: string; code: string }) {
    const { partsMessage, code } = props;
    const md = new Remarkable('commonmark', { breaks: true });
    const { t } = useTranslation('pages');

    return (
        <div className='appliance-parts'>
            <span
                dangerouslySetInnerHTML={{
                    __html: partsMessage,
                }}
            />
            <Button
                link={`/archives/${code.toLowerCase()}/parts`}
                label={t('appliance.see-parts')}
                iconRight={arrowIcon}
            />
        </div>
    );
}

type ApplianceDetailsProps = {
    appliance: any;
    requestedSection?: string;
};

const ApplianceDetails = (props: ApplianceDetailsProps) => {
    const { appliance, requestedSection } = props;
    const code = appliance.code;
    const { currentLocale } = usePageLocalization();
    const isUSA = currentLocale === 'en_US';

    const positionIcon = (
        <Icon
            d={
                'M6.056,17.637C.948,10.232,0,9.472,0,6.75a6.75,6.75,0,0,1,13.5,0c0,2.722-.948,3.482-6.056,10.887a.844.844,0,0,1-1.387,0ZM6.75,9.562A2.812,2.812,0,1,0,3.937,6.75,2.812,2.812,0,0,0,6.75,9.562Z'
            }
            size='18'
            viewbox='0 0 13.5 18'
            transform='translate(0 0)'
        />
    );
    const currentDevice = useMobileDetect();
    const [activeSection, setActiveSection] = useState(
        currentDevice.isMobile() ? 'unselected' : 'Options'
    );
    const [page, setPage] = useState(1);
    const { t } = useTranslation('templatePages');
    const allOptions = useStaticQuery(
        graphql`
            query {
                options: allProductsJson {
                    nodes {
                        code
                        options {
                            code
                            title {
                                en_CA
                                en_US
                                fr_CA
                            }
                            images
                            documents {
                                en_CA
                                en_US
                                fr_CA
                            }
                        }
                        attributes {
                            options_requise {
                                values {
                                    fr_CA
                                    en_US
                                    en_CA
                                }
                            }
                        }
                    }
                }
                parts: allProductsJson {
                    nodes {
                        code
                        parts {
                            code
                            title {
                                en_CA
                                en_US
                                fr_CA
                            }
                            images
                        }
                    }
                }
                allSpecs: allProductsJson(
                    filter: {
                        attributes: {
                            specification_notes: {
                                group: { eq: "Appliance performance" }
                            }
                        }
                    }
                ) {
                    nodes {
                        attributes {
                            specification_notes {
                                name {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                    id
                                }
                            }
                        }
                        code
                    }
                }
                applianceParts: allProductsJson(
                    filter: { code: { eq: "occ_appliance-parts" } }
                ) {
                    nodes {
                        code
                        attributes {
                            content {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                valuesMarkdownRemark {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                        }
                    }
                }
            }
        `
    );

    useEffect(() => {
        if (requestedSection) {
            setActiveSection(requestedSection);
        }
    }, [requestedSection]);

    useEffect(() => {
        if (currentDevice.isMobile() && activeSection !== 'unselected') {
            document
                .getElementById('scroll-appliance-summary')
                ?.scrollIntoView({
                    behavior: 'instant',
                    block: 'start',
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const partsMessage =
        allOptions.applianceParts.nodes?.[0]?.attributes?.content
            ?.valuesMarkdownRemark?.[0][currentLocale];

    function renderCurrentSection() {
        switch (activeSection) {
            case t('appliance-page.option-title'):
                return (
                    <ApplianceOptions
                        code={code}
                        options={
                            allOptions.options.nodes.filter(
                                (option: any) => option.code === code
                            )[0]?.options
                        }
                        page={page}
                        setPage={setPage}
                        options_requise={
                            allOptions.options.nodes.filter(
                                (option: any) => option.code === code
                            )[0]?.attributes?.options_requise?.values
                        }
                    />
                );
            case t('appliance-page.specs-title'):
                return (
                    <ApplianceSpecs
                        appliance={appliance}
                        specifications={allOptions?.allSpecs}
                    />
                );
            case t('appliance-page.manual-title'):
                return (
                    <ApplianceManuals
                        documents={appliance?.documents}
                        applianceCode={code}
                    />
                );
            case t('appliance-page.part-title'):
                return (
                    <ApplianceParts
                        partsMessage={partsMessage}
                        code={appliance?.code}
                    />
                );
        }
    }

    return (
        <div id='appliance-details' className='appliance-details'>
            <div className='appliance-details-titles'>
                <ApplianceDetailsSection
                    title={t('appliance-page.option-title')}
                    toggle={true}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    setPage={setPage}
                    renderCurrentSection={renderCurrentSection}
                />

                <ApplianceDetailsSection
                    title={t('appliance-page.specs-title')}
                    toggle={true}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    renderCurrentSection={renderCurrentSection}
                />

                <ApplianceDetailsSection
                    title={t('appliance-page.manual-title')}
                    toggle={true}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    renderCurrentSection={renderCurrentSection}
                />

                <ApplianceDetailsSection
                    title={t('appliance-page.part-title')}
                    toggle={true}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    setPage={setPage}
                    renderCurrentSection={renderCurrentSection}
                />

                {!isUSA && (
                    <ApplianceDetailsSection
                        title={t('appliance-page.retailer-title')}
                        icon={positionIcon}
                        link='/find-retailer/'
                    />
                )}
            </div>

            {!currentDevice.isMobile() && (
                <div className='appliance-details-content'>
                    {renderCurrentSection()}
                </div>
            )}
        </div>
    );
};

export default ApplianceDetails;
